/** Public recovery marker: never contains a password, mnemonic, or private key. */
export interface PendingProfileCreation {
	version: 1;
	master: string;
	bapId: string;
	index: number;
	name: string;
	stage: "allocate" | "register" | "cloud";
}
type MarkerStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
const active = new Map<string, Promise<string>>();
export function profileCreationMarkerKey(master: string): string {
	return `sigma-profile-creation:${master}`;
}
export function readPendingProfileCreation(
	storage: MarkerStorage,
	master: string
): PendingProfileCreation | null {
	const raw = storage.getItem(profileCreationMarkerKey(master));
	if (!raw) {
		return null;
	}
	const value = JSON.parse(raw) as PendingProfileCreation;
	if (
		value?.version !== 1 ||
		value.master !== master ||
		typeof value.name !== "string" ||
		typeof value.bapId !== "string" ||
		!Number.isInteger(value.index) ||
		value.index < 0 ||
		value.index > 0x7f_ff_ff_ff ||
		!["allocate", "register", "cloud"].includes(value.stage)
	) {
		throw new Error("Pending profile creation marker is invalid");
	}
	return value;
}
export interface ProfileCreationOperations {
	storage: MarkerStorage;
	/** Proposed next peer, computed in memory before allocating it. */
	proposed: Omit<PendingProfileCreation, "version" | "stage">;
	contains: (pending: PendingProfileCreation) => boolean;
	allocate: (pending: PendingProfileCreation) => Promise<string>;
	register: (pending: PendingProfileCreation) => Promise<void>;
	cloud: () => Promise<void>;
}
async function runCreation(ops: ProfileCreationOperations): Promise<string> {
	const key = profileCreationMarkerKey(ops.proposed.master);
	let pending = readPendingProfileCreation(ops.storage, ops.proposed.master);
	if (pending && pending.name !== ops.proposed.name) {
		throw new Error(
			`Finish creating ${pending.name} before starting another profile`
		);
	}
	if (!pending) {
		pending = { ...ops.proposed, version: 1, stage: "allocate" };
		ops.storage.setItem(key, JSON.stringify(pending));
	}
	if (pending.stage === "allocate") {
		if (
			!ops.contains(pending) &&
			(await ops.allocate(pending)) !== pending.bapId
		) {
			throw new Error("Allocated identity did not match pending profile");
		}
		pending = { ...pending, stage: "register" };
		ops.storage.setItem(key, JSON.stringify(pending));
	}
	if (!ops.contains(pending)) {
		throw new Error("Pending profile is absent from this master backup");
	}
	if (pending.stage === "register") {
		await ops.register(pending);
		pending = { ...pending, stage: "cloud" };
		ops.storage.setItem(key, JSON.stringify(pending));
	}
	if (!ops.contains(pending)) {
		throw new Error("Master changed during profile creation");
	}
	await ops.cloud();
	if (!ops.contains(pending)) {
		throw new Error("Master changed during cloud sync");
	}
	ops.storage.removeItem(key);
	return pending.bapId;
}
/** A failed stage retains its exact peer; repeating this operation never silently chooses another. */
export async function resumeProfileCreation(
	ops: ProfileCreationOperations
): Promise<string> {
	const key = profileCreationMarkerKey(ops.proposed.master);
	if (active.has(key)) {
		throw new Error("Profile creation is already in progress");
	}
	const operation = runCreation(ops);
	active.set(key, operation);
	try {
		return await operation;
	} finally {
		active.delete(key);
	}
}
